import { useEffect, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';

import { KYCStatus, KycStatusResponse } from 'services/types/kycStatus';

type KycStatusState = {
  loading: boolean;
  data?: KycStatusResponse;
  error?: unknown;
  statusKey?: string;
};

const useKycStatus = (stripeVerificationStatus?: string, pause?: boolean) => {
  const dataProvider = useDataProvider();
  const [state, setState] = useState<KycStatusState>({ loading: false });

  const normalizedStatus = useMemo(() => {
    return stripeVerificationStatus?.toUpperCase() as KYCStatus | undefined;
  }, [stripeVerificationStatus]);

  const statusKey = normalizedStatus || stripeVerificationStatus || '';
  const currentData = state.statusKey === statusKey ? state.data : undefined;
  const status = currentData?.status ?? normalizedStatus;
  const secondsLeft = currentData?.secondsLeft;
  const hasCountdown = typeof secondsLeft === 'number';
  const isVerificationRequired = status !== undefined ? status !== KYCStatus.COMPLETED : false;
  const shouldFetch =
    !pause && (normalizedStatus !== KYCStatus.COMPLETED || stripeVerificationStatus !== undefined);

  useEffect(() => {
    let isActive = true;

    if (!shouldFetch) {
      setState((prevState) => {
        if (prevState.statusKey === statusKey && prevState.loading === false && !prevState.error) {
          return prevState;
        }

        return {
          loading: false,
          data: prevState.statusKey === statusKey ? prevState.data : undefined,
          error: undefined,
          statusKey,
        };
      });
      return;
    }

    setState((prevState) => ({
      loading: true,
      data: prevState.statusKey === statusKey ? prevState.data : undefined,
      error: undefined,
      statusKey,
    }));

    dataProvider
      .get('kycStatus')
      .then(({ data }) => {
        if (!isActive) return;

        const secondsLeft = typeof data?.secondsLeft === 'number' ? data.secondsLeft : null;
        const status = data?.status as KYCStatus;

        setState({
          loading: false,
          data: {
            status,
            secondsLeft,
          },
          error: undefined,
          statusKey,
        });
      })
      .catch((error) => {
        if (!isActive) return;
        setState((prevState) => ({
          loading: false,
          data: prevState.statusKey === statusKey ? prevState.data : undefined,
          error,
          statusKey,
        }));
      });

    return () => {
      isActive = false;
    };
  }, [dataProvider, pause, shouldFetch, statusKey]);

  return {
    loading: state.loading,
    error: state.error,
    status,
    secondsLeft,
    hasCountdown,
    isVerificationRequired,
  };
};

export default useKycStatus;
